// schema.org JSON-LD builders. Keep them in sync with what is visible on the page:
// Google ignores (or penalises) structured data that the user can't see.
import { SITE } from "../config/site";
import { LANGS, localizePath } from "../i18n";

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://tehnocin.com").replace(/\/$/, "");

export const absoluteUrl = (path = "/") => (/^https?:/.test(path) ? path : `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`);

const ORG_ID = `${SITE_URL}/#organization`;

export const PROJECT_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Beočinski trg",
  addressLocality: "Beočin",
  postalCode: "21300",
  addressRegion: "Južnobački okrug",
  addressCountry: "RS",
};

// The developer itself. GeneralContractor + RealEstateAgent are both LocalBusiness subtypes,
// which is what local search (Maps, "stanovi Beočin") keys on.
export function organization() {
  return {
    "@context": "https://schema.org",
    "@type": ["GeneralContractor", "RealEstateAgent"],
    "@id": ORG_ID,
    name: SITE.name,
    legalName: SITE.legalName,
    url: SITE_URL,
    logo: absoluteUrl("/logo512.png"),
    image: absoluteUrl("/og-image.jpg"),
    telephone: `+${SITE.phoneIntl}`,
    email: SITE.email,
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Železnička 11a",
      addressLocality: "Beočin",
      postalCode: "21300",
      addressCountry: "RS",
    },
    areaServed: [
      { "@type": "City", name: "Beočin" },
      { "@type": "City", name: "Novi Sad" },
    ],
    hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SITE.officeMapQuery)}`,
    sameAs: Object.values(SITE.social),
    knowsLanguage: ["sr", "en", "ru"],
  };
}

export function website(lang) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: absoluteUrl(localizePath("/", lang)),
    name: SITE.name,
    inLanguage: LANGS[lang].hreflang,
    publisher: { "@id": ORG_ID },
  };
}

export function breadcrumbs(items, lang) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(localizePath(item.path, lang)),
    })),
  };
}

// One building (lamela) as an ApartmentComplex with its availability.
export function apartmentComplex(project, { lang, description, free }) {
  return {
    "@context": "https://schema.org",
    "@type": "ApartmentComplex",
    "@id": `${SITE_URL}/katalog/${project.slug}#building`,
    name: `${project.identifier}, ${project.naziv}`,
    description: description || undefined,
    url: absoluteUrl(localizePath(`/katalog/${project.slug}`, lang)),
    address: { ...PROJECT_ADDRESS, streetAddress: project.adresa?.split(",")[0] || PROJECT_ADDRESS.streetAddress },
    numberOfAccommodationUnits: project.kapacitet_stanovi || undefined,
    numberOfAvailableAccommodationUnits: free,
    image: project.slike?.length ? project.slike.slice(0, 3) : undefined,
    containedInPlace: { "@type": "Place", name: "Beočinski trg, Beočin", address: PROJECT_ADDRESS },
    provider: { "@id": ORG_ID },
  };
}

export function faqPage(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

// The list of available units on a catalogue page (an ItemList of Accommodation).
export function unitList(units, { lang, describe }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    numberOfItems: units.length,
    itemListElement: units.slice(0, 50).map((unit, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": unit.tip === "lokal" ? "Accommodation" : "Apartment",
        name: describe(unit),
        floorSize: unit.povrsina ? { "@type": "QuantitativeValue", value: parseFloat(String(unit.povrsina).replace(",", ".")), unitCode: "MTK" } : undefined,
        numberOfRooms: parseFloat(unit.struktura) || undefined,
        url: absoluteUrl(localizePath("/katalog", lang)),
      },
    })),
  };
}
